import { type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BuilderTabPanel, type BuilderPanels } from "../builder/BuilderPanel";
import { VaultTabPanel, type VaultPanels } from "../vault/VaultPanel";
import { CodexTabPanel, type CodexPanels, type CodexCounts } from "./CodexTabPanel";
import { PlaceholderPanel } from "./PlaceholderPanel";
import { BOTTOM_TABS, formatCount, type BottomDockCount, type BottomTab } from "./shellState";

const CorpusTabPanel = () => (
  <div className="h-full overflow-auto p-3">
    <PlaceholderPanel
      title="My Library"
      bundle="H2"
      description="Your folders and pasted/uploaded source documents for grounding the AI chat. Coming soon."
    />
  </div>
);

type Panels = {
  codex?: CodexPanels;
  builder?: BuilderPanels;
  vault?: VaultPanels;
  corpus?: ReactNode;
};

type Counts = {
  codex?: CodexCounts;
  builder?: BottomDockCount;
  vault?: BottomDockCount;
  corpus?: BottomDockCount;
};

interface Props {
  workspaceId?: string;
  bottomDockTab: BottomTab;
  setBottomDockTab: (t: BottomTab | null) => void;
  panels?: Panels;
  counts?: Counts;
}

export function BottomDockContent({ workspaceId, bottomDockTab, setBottomDockTab, panels, counts }: Props) {
  const tab = BOTTOM_TABS.find((t) => t.id === bottomDockTab)!;
  const Icon = tab.Icon;
  const rawCount = counts?.[tab.id];
  const countLabel =
    tab.id === "codex" || tab.id === "builder"
      ? null
      : formatCount(rawCount as BottomDockCount | undefined);

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-9 shrink-0 items-center justify-between border-b border-border/60 px-3">
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-medium">{tab.label}</span>
          {countLabel && (
            <span className="text-[10px] tabular-nums text-muted-foreground">{countLabel}</span>
          )}
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider">H2</Badge>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => setBottomDockTab(null)}
          aria-label="Collapse workspace panel"
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {bottomDockTab === "codex" ? (
          <CodexTabPanel workspaceId={workspaceId} panels={panels?.codex} counts={counts?.codex} />
        ) : bottomDockTab === "builder" ? (
          <BuilderTabPanel workspaceId={workspaceId} panels={panels?.builder} />
        ) : bottomDockTab === "vault" ? (
          <VaultTabPanel workspaceId={workspaceId} panels={panels?.vault} />
        ) : (
          (panels?.corpus as ReactNode) ?? <CorpusTabPanel />
        )}
      </div>
    </div>
  );
}

export type { Panels as BottomDockPanels, Counts as BottomDockCounts };
